import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X } from 'lucide-react';

/**
 * Acima do Drawer (1050) de propósito.
 *
 * O modal é sempre a camada mais efêmera da tela: quando ele abre a partir de um
 * drawer - confirmar exclusão, importar contatos - ele precisa ficar por cima.
 * Com o valor antigo (1000) o modal abria atrás do overlay do drawer, e como esse
 * overlay é `inset: 0`, ele engolia todos os cliques: o modal aparecia meio
 * escondido e nenhum campo ou botão dele respondia.
 *
 * O z-index continua valendo depois do portal: os dois viram filhos de `body`,
 * e 1100 > 1050 mantém a ordem.
 */
const MODAL_Z_INDEX = 1100;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 42, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: ${MODAL_Z_INDEX};
`;

const Dialog = styled.div<{ $width?: string }>`
  width: 100%;
  max-width: ${(p) => p.$width || '520px'};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.surface};
  border-radius: ${(p) => p.theme.radiusLg};
  box-shadow: ${(p) => p.theme.shadowMd};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid ${(p) => p.theme.border};

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Close = styled.button`
  display: inline-flex;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.textMuted};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: ${(p) => p.theme.radius};
  &:hover {
    background: ${(p) => p.theme.lightGray};
  }
`;

const Body = styled.div`
  padding: 1.25rem;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid ${(p) => p.theme.border};
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  width?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

/**
 * Renderizado em `document.body`, e não onde o JSX o coloca.
 *
 * O `Backdrop` é `position: fixed; inset: 0`, o que só significa "a viewport
 * inteira" enquanto NENHUM ancestral tiver `transform`, `filter` ou
 * `perspective` - qualquer um dos três faz o elemento virar o bloco de contenção
 * dos descendentes `fixed`. O `Sheet` do Drawer tem `animation: slideIn ...
 * forwards`, cujos keyframes são `transform: translateX(...)`, e a animação
 * preenchida deixa o transform aplicado depois de terminar. Resultado: um modal
 * aberto de dentro de um drawer media os 760px do drawer em vez da tela, e
 * aparecia centralizado na metade direita - foi o que aconteceu com o "Novo
 * funcionário" na ficha do lojista.
 *
 * Aumentar z-index não resolve isso: o problema é geometria, não empilhamento.
 * Tirar o transform do drawer também não - é ele que faz a animação de entrada.
 * O portal tira o backdrop da subárvore do drawer, e aí `fixed` volta a ser a
 * viewport. É o mesmo caminho que o `SearchableComboBox` já usa para o menu.
 *
 * `document.body` é lido dentro do componente (não no módulo) para que importar
 * este arquivo continue funcionando onde não há DOM.
 */
export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, width, children, footer }) => {
  if (!isOpen || typeof document === 'undefined') return null;
  return createPortal(
    <Backdrop onClick={onClose}>
      <Dialog $width={width} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <Header>
          <h3>{title}</h3>
          <Close type="button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </Close>
        </Header>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Dialog>
    </Backdrop>,
    document.body
  );
};

export default Modal;
